// Modo Revisão da prova encerrada — abre tela cheia com navegação por
// questão (prev/next), sidebar compartilhada com a do Estudo/Prova e
// QuestionCard read-only com feedback verde/vermelho.
//
// Não confundir com:
//   - GabaritoModal (em ProvaResultView): modal pra ver UMA questão.
//   - ProvaWorkspace: prova em andamento, aluno responde.
//
// Read-only. Aluno não pode alterar respostas — só consulta.
//
// URL: /student/banks/:bankId/prova/:provaId/revisao
// Entrada: enterProvaRevisaoView(bankId, provaId)

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  AlignLeft,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Circle,
  Flag,
  Grid3x3,
  Image as ImageIcon,
  List as ListIcon,
  Loader2,
  Search,
  SlidersHorizontal,
  Sparkles,
  Table as TableIcon,
  XCircle,
  X as XIcon
} from "lucide-react";
import { useModalGrowShrink } from "./modalOrigin";
import { DifficultyStars } from "./questionBadges";
import { FilterRow, FilterSection, aggregateCompositeScore } from "./StudyWorkspace";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type EssayResponseState,
  type ProvaState,
  type QuestaoStudy
} from "./api";
import { filterEscopo } from "./ProvaWorkspace";
import { WorkspaceHeader, type WorkspaceHeaderAction } from "./WorkspaceHeader";
import {
  QuestionListSidebar,
  applyAgrupamento as applySharedAgrupamento,
  type QListAgrupamento,
  type QListStatus
} from "./QuestionListSidebar";
import { QuestionCard } from "./QuestionCard";
import { lightboxImg, Lightbox } from "./StudyWorkspace";
import { questaoHasImages, questaoHasTabelas } from "./questionBadges";
import { uiPrefs, toggleQuestionListCollapsed, toggleAiAssistantOpen } from "./uiPrefs";
import { AiMarkdownBlock, CopyAiTextButton } from "./aiMarkdown";
import { friendlyAiError, type FriendlyAiError } from "./friendlyAiError";
import {
  AiAssistant,
  chatHistory,
  activeVerbosity,
  VerbosityChip
} from "./AiAssistant";
import { isPlayerOpen } from "./MusicPlayer";
import {
  readAiExplainCache,
  writeAiExplainCache,
  deleteAiExplainCache,
  fetchAiExplainFromServer,
  saveAiExplainToServer,
  deleteAiExplainFromServer
} from "./aiExplainCache";
import {
  readAiAnalysisCache,
  writeAiAnalysisCache,
  fetchAiAnalysisFromServer
} from "./aiAnalysisCache";

// =====================
// State + routing
// =====================

type Phase = "loading" | "error" | "ready";

interface ProvaRevisaoState {
  bankId: string;
  provaId: string;
  phase: Phase;
  prova: ProvaState | null;
  bank: { id: string; name: string } | null;
  questoes: QuestaoStudy[];
  /** Respostas dissertativas hidratadas (bucket estudo.essayResponses).
   *  Sem isso, statusOf/contagens ignoram dissertativas. */
  essayResponses: Record<string, EssayResponseState>;
  currentIdx: number;
  errorMsg: string | null;
}

export const provaRevisaoView = signal<ProvaRevisaoState | null>(null);
const revisaoMapOpen = signal<boolean>(false);
const revisaoFiltersOpen = signal<boolean>(false);
const revisaoAgrupamento = signal<QListAgrupamento>("fonte");

// Filtros do modo Revisão. Mesmo padrão visual + escopo do ProvaFilterPanel
// (drawer à direita, FilterSection/FilterRow, busca, dificuldade, conteúdo,
// tema, fonte). Diferença chave: aqui o status é acerto/erro/anulada/pendente
// (já temos gabarito), enquanto em prova ativa é respondida/pendente. Sessão
// — não persiste entre logins.
type RevisaoStatusFilter = QuestaoStatus;
interface RevisaoFiltersState {
  status: RevisaoStatusFilter[]; // vazio = todos
  temas: string[]; // vazio = todos
  fontes: string[]; // vazio = todos
  dificuldade: number[]; // vazio = todos
  somenteCaso: boolean;
  somenteImagem: boolean;
  somenteTabela: boolean;
  busca: string;
}
const REVISAO_FILTERS_DEFAULT: RevisaoFiltersState = {
  status: [],
  temas: [],
  fontes: [],
  dificuldade: [],
  somenteCaso: false,
  somenteImagem: false,
  somenteTabela: false,
  busca: ""
};
const revisaoFilters = signal<RevisaoFiltersState>({ ...REVISAO_FILTERS_DEFAULT });
function updateRevisaoFilters(patch: Partial<RevisaoFiltersState>) {
  revisaoFilters.value = { ...revisaoFilters.value, ...patch };
}
function clearRevisaoFilters() {
  revisaoFilters.value = { ...REVISAO_FILTERS_DEFAULT };
}
function countRevisaoFiltersActive(): number {
  const f = revisaoFilters.value;
  let n = 0;
  if (f.status.length > 0) n++;
  if (f.temas.length > 0) n++;
  if (f.fontes.length > 0) n++;
  if (f.dificuldade.length > 0) n++;
  if (f.somenteCaso) n++;
  if (f.somenteImagem) n++;
  if (f.somenteTabela) n++;
  if (f.busca.trim()) n++;
  return n;
}
// Aplica TODOS os filtros (não só status). Mesma lógica do matchesProvaFiltro
// adaptada pro vocabulário acerto/erro do Revisar.
function matchesRevisaoFiltro(
  q: QuestaoStudy,
  st: QuestaoStatus,
  f: RevisaoFiltersState
): boolean {
  if (f.status.length > 0 && !f.status.includes(st)) return false;
  if (f.temas.length > 0 && (!q.tema || !f.temas.includes(q.tema))) return false;
  if (f.fontes.length > 0 && (!q.fonte || !f.fontes.includes(q.fonte))) return false;
  if (f.dificuldade.length > 0 && f.dificuldade.length < 5) {
    const d = Number(q.dificuldade ?? 0);
    if (!f.dificuldade.includes(d)) return false;
  }
  const temImg =
    (typeof q.imagem_url === "string" && q.imagem_url) ||
    (Array.isArray(q.imagens) && q.imagens.length > 0);
  if (f.somenteImagem && !temImg) return false;
  if (f.somenteCaso && !q.caso) return false;
  if (f.somenteTabela && !(Array.isArray(q.tabelas) && q.tabelas.length > 0)) return false;
  if (f.busca.trim()) {
    const term = f.busca.toLowerCase();
    const hay = [
      q.pergunta ?? "",
      q.caso ?? "",
      ...(Array.isArray(q.opcoes) ? q.opcoes.map((o) => (typeof o === "string" ? o : "")) : [])
    ]
      .join(" ")
      .toLowerCase();
    if (!hay.includes(term)) return false;
  }
  return true;
}

const REVISAO_PATH_RE = /^\/student\/banks\/([^/]+)\/prova\/([^/]+)\/revisao\/?$/;

export function enterProvaRevisaoView(
  bankId: string,
  provaId: string,
  opts: { pushHistory?: boolean; startIdx?: number } = {}
) {
  // Reset filtros ao trocar de banco OU de prova — pedido Karol 2026-05-24:
  // filtros não devem vazar entre revisões. Também reseta agrupamento e
  // modais auxiliares pra estado neutro.
  const prev = provaRevisaoView.value;
  if (!prev || prev.bankId !== bankId || prev.provaId !== provaId) {
    clearRevisaoFilters();
    revisaoFiltersOpen.value = false;
    revisaoMapOpen.value = false;
    revisaoAgrupamento.value = "fonte";
  }
  provaRevisaoView.value = {
    bankId,
    provaId,
    phase: "loading",
    prova: null,
    bank: null,
    questoes: [],
    essayResponses: {},
    currentIdx: opts.startIdx ?? 0,
    errorMsg: null
  };
  if (opts.pushHistory !== false) {
    const url = `/student/banks/${encodeURIComponent(bankId)}/prova/${encodeURIComponent(
      provaId
    )}/revisao`;
    if (window.location.pathname !== url) {
      window.history.pushState({ provaRevisaoId: provaId }, "", url);
    }
  }
  void load(bankId, provaId);
}

export function exitProvaRevisaoView() {
  provaRevisaoView.value = null;
  revisaoMapOpen.value = false;
}

async function load(bankId: string, provaId: string) {
  try {
    const detail = await api.bankProvaDetail(bankId, provaId);
    if (provaRevisaoView.value?.provaId !== provaId) return;
    provaRevisaoView.value = {
      ...provaRevisaoView.value!,
      phase: "ready",
      prova: detail.prova,
      bank: { id: detail.bank.id, name: detail.bank.name },
      questoes: detail.questoes,
      essayResponses: detail.essayResponses ?? {},
      errorMsg: null
    };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    provaRevisaoView.value = {
      ...provaRevisaoView.value!,
      phase: "error",
      errorMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

let popstateBound = false;
export function bindProvaRevisaoPopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    const m = REVISAO_PATH_RE.exec(window.location.pathname);
    if (m) {
      const bankId = decodeURIComponent(m[1]);
      const provaId = decodeURIComponent(m[2]);
      if (
        !provaRevisaoView.value ||
        provaRevisaoView.value.provaId !== provaId
      ) {
        enterProvaRevisaoView(bankId, provaId, { pushHistory: false });
      }
    } else {
      if (provaRevisaoView.value) provaRevisaoView.value = null;
    }
  });
}

export function restoreProvaRevisaoFromUrl() {
  if (typeof window === "undefined") return;
  const m = REVISAO_PATH_RE.exec(window.location.pathname);
  if (m) {
    const bankId = decodeURIComponent(m[1]);
    const provaId = decodeURIComponent(m[2]);
    enterProvaRevisaoView(bankId, provaId, { pushHistory: false });
  }
}

// =====================
// Helpers (mesmas regras do ResultView — anulada conta como respondida)
// =====================

function isAnuladaQ(q: QuestaoStudy): boolean {
  return Number(q.correta) === 0;
}

type QuestaoStatus = "acerto" | "erro" | "anulada" | "pendente";

function statusOf(
  q: QuestaoStudy,
  prova: ProvaState,
  essayResponses: Record<string, EssayResponseState> = {}
): QuestaoStatus {
  if (isAnuladaQ(q)) return "anulada";
  // Dissertativa: só conta se a key foi tocada NESTA prova (Karol 2026-05-24).
  // Sem isso, bucket compartilhado com Study aparecia como respondido.
  const touched = new Set(
    Array.isArray(prova.essayKeysTouched) ? prova.essayKeysTouched : []
  );
  function essayClass(key: string): QuestaoStatus | null {
    if (!touched.has(key)) return null;
    const st = essayResponses[key];
    const hasText = typeof st?.resposta === "string" && st.resposta.trim().length > 0;
    if (!hasText) return null;
    if (st?.nota == null) return "pendente";
    return st.nota >= 7 ? "acerto" : "erro";
  }
  if (q.tipo === "essay") {
    return essayClass(String(q.id)) ?? "pendente";
  }
  if (Array.isArray(q.items) && q.items.length > 0) {
    // Composta: agrega via aggregateCompositeScore (mesma função que o
    // QuestionCard usa pra mostrar a nota ponderada). Items têm `peso`
    // (notaQuestao) — média SIMPLES dava errado pra prova com peso 1×25%
    // + 3×75%. Bug Luís 2026-05-25: Q1 com nota ponderada 7,88 ficava
    // vermelha na lista (era pra ser verde). Threshold ≥ 7.
    const agg = aggregateCompositeScore(q, prova.respostas as unknown as Record<string, number>, essayResponses);
    if (agg.pendente) return "pendente";
    return agg.notaFinal >= 7 ? "acerto" : "erro";
  }
  const r = prova.respostas[q.id];
  if (!r || r.opcao == null) return "pendente";
  const corr = (Number(q.correta) || 0) - 1;
  return r.opcao === corr ? "acerto" : "erro";
}

// Adapter: status local → QListStatus da sidebar compartilhada.
function statusToQList(s: QuestaoStatus): QListStatus {
  if (s === "acerto") return "correct";
  if (s === "erro") return "wrong";
  if (s === "anulada") return "anulada";
  return "pending";
}

function gotoIdx(idx: number, total: number) {
  const ws = provaRevisaoView.value;
  if (!ws) return;
  const clamped = Math.max(0, Math.min(total - 1, idx));
  provaRevisaoView.value = { ...ws, currentIdx: clamped };
}

// =====================
// UI
// =====================

export function ProvaRevisaoViewComp() {
  const state = provaRevisaoView.value;
  if (!state) return null;
  if (state.phase === "loading") return <RevisaoLoading />;
  if (state.phase === "error") return <RevisaoError msg={state.errorMsg} />;
  return (
    <>
      <RevisaoReady state={state} />
      {revisaoMapOpen.value && <RevisaoMapModal state={state} />}
      {lightboxImg.value && <Lightbox image={lightboxImg.value} />}
    </>
  );
}

function RevisaoLoading() {
  return (
    <div class="space-y-4">
      <Skeleton className="h-20 w-full rounded-md" />
      <div class="flex gap-4">
        <Skeleton className="hidden h-[600px] w-80 rounded-md lg:block" />
        <div class="flex-1 space-y-3">
          <Skeleton className="h-8 w-2/3 rounded-md" />
          <Skeleton className="h-24 rounded-md" />
          <Skeleton className="h-10 rounded-md" />
          <Skeleton className="h-10 rounded-md" />
          <Skeleton className="h-10 rounded-md" />
        </div>
      </div>
    </div>
  );
}

function RevisaoError({ msg }: { msg: string | null }) {
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Não consegui carregar a revisão</h2>
        {msg && <p class="text-sm text-muted-foreground">{msg}</p>}
        <button
          type="button"
          onClick={() => {
            exitProvaRevisaoView();
            window.history.back();
          }}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
        >
          Voltar
        </button>
      </div>
    </div>
  );
}

function RevisaoReady({ state }: { state: ProvaRevisaoState }) {
  const prova = state.prova!;
  // Subscreve aos filtros pro re-render disparar quando aluno toggla.
  const activeFilters = revisaoFilters.value;
  // Escopo unsorted (ordem original de criação da prova). Filtragem por
  // status aplicada DEPOIS — sem isso, a contagem do header e o índice
  // navegacional não batem com a sidebar filtrada.
  const escopoFull = useMemo(() => {
    if (prova.escopoQuestaoIds.length === 0) return state.questoes;
    return filterEscopo(state.questoes, prova.escopoQuestaoIds);
  }, [state.questoes, prova]);
  const escopo = useMemo(() => {
    if (countRevisaoFiltersActive() === 0) return escopoFull;
    return escopoFull.filter((q) => {
      const st = statusOf(q, prova, state.essayResponses);
      return matchesRevisaoFiltro(q, st, activeFilters);
    });
  }, [escopoFull, activeFilters, prova, state.essayResponses]);

  // Contagens pro header.
  const counts = useMemo(() => {
    let c = 0;
    let w = 0;
    let p = 0;
    let a = 0;
    for (const q of escopo) {
      const st = statusOf(q, prova, state.essayResponses);
      if (st === "anulada") a++;
      else if (st === "acerto") c++;
      else if (st === "erro") w++;
      else p++;
    }
    return { acertos: c, erros: w, pendentes: p, anuladas: a, total: escopo.length };
  }, [escopo, prova]);

  if (escopo.length === 0) {
    // Caso 1: escopo VAZIO de origem (banco sem matches) → mensagem genérica
    // Caso 2: filtros derrubaram tudo → oferece resetar
    const filtroCortouTudo =
      escopoFull.length > 0 && countRevisaoFiltersActive() > 0;
    return (
      <div class="grid place-items-center py-16">
        <div class="max-w-md space-y-3 rounded-md border border-border bg-card/40 px-6 py-8 text-center">
          <p class="text-sm text-muted-foreground">
            {filtroCortouTudo
              ? "Nenhuma questão bate com os filtros atuais."
              : "Esta prova não tem questões no escopo."}
          </p>
          {filtroCortouTudo && (
            <button
              type="button"
              onClick={clearRevisaoFilters}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
            >
              <SlidersHorizontal width="0.95em" height="0.95em" />
              Limpar filtros
            </button>
          )}
        </div>
      </div>
    );
  }

  const idx = Math.max(0, Math.min(escopo.length - 1, state.currentIdx));
  const q = escopo[idx];
  if (!q) return null;
  const status = statusOf(q, prova, state.essayResponses);

  // Atalhos de teclado
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (
        ev.target instanceof HTMLInputElement ||
        ev.target instanceof HTMLTextAreaElement
      )
        return;
      // Player aberto consome TODOS os atalhos (mesmo padrão do Study e Prova).
      // Sem isso, L do player também togglava a lista de questões aqui.
      // Reportado Karol 2026-05-24.
      if (isPlayerOpen.value) return;
      // Atalhos com Ctrl/meta/alt são pra theme toggle e outras combinações
      // do navegador — não interceptar.
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key === "ArrowLeft") gotoIdx(idx - 1, escopo.length);
      else if (ev.key === "ArrowRight") gotoIdx(idx + 1, escopo.length);
      else if (ev.key === "Escape") backToResult(state);
      else if (ev.key === "m" || ev.key === "M") revisaoMapOpen.value = true;
      else if (ev.key === "l" || ev.key === "L") toggleQuestionListCollapsed();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, escopo.length, state]);

  // Ordenação pra sidebar. Preserva o ÍNDICE ORIGINAL (Q1, Q33, etc.) mesmo
  // quando há filtro ativo — itens fora do filtro ficam dimmed (opacity-50)
  // via `visibleIds` em vez de sumirem renumerados. Mesmo padrão do
  // ProvaWorkspace. [[feedback Karol 2026-05-24: "manter o número da questão
  // quando o filtro for aplicado"]]
  const sortedQuestoes = useMemo(
    () => applySharedAgrupamento(escopoFull, revisaoAgrupamento.value),
    [escopoFull, revisaoAgrupamento.value]
  );
  const sortedCurrentIdx = useMemo(
    () => sortedQuestoes.findIndex((sq) => sq.id === q.id),
    [sortedQuestoes, q.id]
  );
  const visibleIds = useMemo(
    () => new Set(escopo.map((eq) => String(eq.id))),
    [escopo]
  );
  // Número estável de cada questão (Q42 fica Q42 mesmo após filtro/sort).
  // Calculado a partir de escopoFull, que é a ordem FONTE original da prova.
  // Usa numero_original se a questão tiver — respeita numeração custom do
  // banco (ex: ENEM "Q33" do gabarito oficial). Senão, posição em escopoFull.
  const numOfQ = useMemo(() => {
    const map = new Map<string, number | string>();
    escopoFull.forEach((eq, i) => {
      const n = (eq as any).numero_original;
      map.set(String(eq.id), n != null && n !== "" ? n : i + 1);
    });
    return (q: any) => map.get(String(q.id)) ?? "?";
  }, [escopoFull]);

  const listSide = uiPrefs.value.questionListSide;
  const tempoMs =
    prova.tempoPorQuestao?.[q.id] ?? prova.respostas[q.id]?.tempoMs ?? 0;

  return (
    <div class="space-y-4">
      <RevisaoHeader state={state} counts={counts} currentIdx={idx} total={escopo.length} />
      <UngradedEssaysBanner state={state} />
      <AiAnalysisBanner bankId={state.bankId} provaId={state.provaId} />
      {/* Layout idêntico ao StudyWorkspace: ORDEM IMPORTA — [list, main, AI].
          Com flex-row: list LEFT, main CENTER, AI RIGHT.
          Com flex-row-reverse: AI LEFT, main CENTER, list RIGHT.
          Sem essa ordem, list e AI acabam juntos no mesmo lado (main vira
          flex-1 e empurra os outros dois pro lado oposto). Bug 2026-05-24. */}
      <div
        class={`flex flex-col gap-4 lg:items-start ${
          listSide === "right" ? "lg:flex-row-reverse" : "lg:flex-row"
        }`}
      >
        <QuestionListSidebar
          questoes={sortedQuestoes}
          currentIdx={Math.max(0, sortedCurrentIdx)}
          onPick={(sortedIdx) => {
            const targetQ = sortedQuestoes[sortedIdx];
            if (!targetQ) return;
            // Tenta navegar via escopo filtrado primeiro (mantém UX coerente
            // com filtro). Se a questão está fora do filtro, navega pela
            // posição em escopoFull (que é a fonte do índice navegacional).
            const unsortedIdx = escopo.findIndex((eq) => eq.id === targetQ.id);
            if (unsortedIdx >= 0) {
              gotoIdx(unsortedIdx, escopo.length);
            } else {
              const fullIdx = escopoFull.findIndex((eq) => eq.id === targetQ.id);
              if (fullIdx >= 0) gotoIdx(fullIdx, escopoFull.length);
            }
          }}
          statusOf={(qq) => statusToQList(statusOf(qq, prova, state.essayResponses))}
          tempoMsOf={(qq) =>
            prova.tempoPorQuestao?.[qq.id] ??
            prova.respostas[qq.id]?.tempoMs ??
            0
          }
          agrupamento={revisaoAgrupamento.value}
          onAgrupamentoChange={(a) => (revisaoAgrupamento.value = a)}
          visibleIds={visibleIds}
          numOf={numOfQ}
          countLabel={`${escopo.length}/${escopoFull.length}`}
        />
        <div class="min-w-0 flex-1 space-y-4">
          <QuestionCard
            q={q}
            selected={prova.respostas[q.id]?.opcao ?? undefined}
            feedback="answered"
            showCorrectWhenWrong
            showExplanation
            tempoMs={tempoMs}
            // Dissertativas: passa o bucket essayResponses + bankId pra
            // QuestionCard renderizar resposta salva + nota IA. Sem isso,
            // a textarea fica vazia mesmo com dados no DB. Bug Luís
            // 2026-05-25: prova prova_1779660708087 (8 dissertativas
            // com nota) aparecia sem resposta e sem nota na Revisão.
            bankId={state.bankId}
            essayResponses={state.essayResponses}
          />
          {status !== "acerto" && (
            <AiExplainBlock
              q={q}
              respostaIdx={prova.respostas[q.id]?.opcao ?? null}
              corretaIdx={(Number(q.correta) || 0) - 1}
              status={status}
              bankId={state.bankId}
              provaId={state.provaId}
            />
          )}
          <RevisaoNav
            idx={idx}
            total={escopo.length}
            onPrev={() => gotoIdx(idx - 1, escopo.length)}
            onNext={() => gotoIdx(idx + 1, escopo.length)}
          />
        </div>
        {/* Assistente IA + Anotações — mesma sidebar do Modo Estudo, mas
            ativa só quando a prova está finalizada. A IA já recebe
            `respostaAluno` e o backend marca "(acertou)" ou "(errou)" no
            system prompt via system-prompt.ts. */}
        <AiAssistant
          question={q}
          respostaAluno={prova.respostas[q.id]?.opcao ?? null}
          bankId={state.bankId}
        />
      </div>
      {revisaoFiltersOpen.value && (
        <RevisaoFiltersModal
          questoes={escopoFull}
          prova={prova}
          essayResponses={state.essayResponses}
        />
      )}
    </div>
  );
}

// =====================
// Painel de filtros do modo Revisão. Drawer à direita seguindo o MESMO padrão
// visual + escopo do ProvaFilterPanel (busca + status + dificuldade + conteúdo
// + tema + fonte), reusando FilterSection/FilterRow/DifficultyStars do Study.
// =====================

function RevisaoFiltersModal({
  questoes,
  prova,
  essayResponses
}: {
  questoes: QuestaoStudy[];
  prova: ProvaState;
  essayResponses: Record<string, EssayResponseState>;
}) {
  const isOpen = revisaoFiltersOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } =
    useModalGrowShrink(isOpen, () => (revisaoFiltersOpen.value = false));
  if (!isOpen) return null;
  const f = revisaoFilters.value;
  const activeCount = countRevisaoFiltersActive();

  // Temas/fontes únicos do ESCOPO da prova (não do banco todo).
  const temas: string[] = [];
  const fontes: string[] = [];
  for (const q of questoes) {
    if (q.tema && !temas.includes(q.tema)) temas.push(q.tema);
    if (q.fonte && !fontes.includes(q.fonte)) fontes.push(q.fonte);
  }

  // Counts de status — considerando os OUTROS filtros aplicados (sem o status
  // entrar na própria contagem, senão zeraria os checkboxes desmarcados).
  const counts = { acerto: 0, erro: 0, anulada: 0, pendente: 0 };
  for (const q of questoes) {
    const st = statusOf(q, prova, essayResponses);
    if (!matchesRevisaoFiltro(q, st, { ...f, status: [] })) continue;
    counts[st]++;
  }

  function toggleArrayItem<T>(arr: T[], item: T): T[] {
    return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
  }
  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) requestClose();
  }

  return (
    <div
      class="modal-overlay-fade fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm"
      data-closing={isClosing ? "true" : "false"}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Filtros da revisão"
    >
      <aside
        ref={ref}
        class="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col overflow-y-auto scroll-thin border-l border-border bg-card p-4 shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-sm font-bold">
            <SlidersHorizontal width="1.3em" height="1.3em" className="text-primary" />
            Filtros da revisão
            {activeCount > 0 && (
              <span class="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
                {activeCount}
              </span>
            )}
          </h2>
          <div class="flex items-center gap-1.5">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearRevisaoFilters}
                class="rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                Limpar
              </button>
            )}
            <button
              type="button"
              aria-label="Fechar"
              onClick={requestClose}
              class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          </div>
        </header>

        <div class="space-y-4">
          {/* Busca */}
          <label class="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-primary">
            <Search width="1.3em" height="1.3em" className="shrink-0 text-muted-foreground" strokeWidth={2} />
            <input
              type="search"
              value={f.busca}
              onInput={(e: Event) =>
                updateRevisaoFilters({ busca: (e.target as HTMLInputElement).value })
              }
              placeholder="Buscar no enunciado…"
              class="min-w-0 flex-1 bg-transparent py-1.5 text-xs placeholder:text-muted-foreground/60 focus:outline-none"
            />
          </label>

          {/* Status — em revisão temos acerto/erro/anulada/pendente */}
          <FilterSection title="Status">
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <CheckCircle2 width="1.1em" height="1.1em" className="text-emerald-600 dark:text-emerald-400" />
                  Acertos
                </span>
              }
              count={counts.acerto}
              checked={f.status.includes("acerto")}
              onToggle={() =>
                updateRevisaoFilters({ status: toggleArrayItem(f.status, "acerto") })
              }
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <XCircle width="1.1em" height="1.1em" className="text-red-600 dark:text-red-400" />
                  Erros
                </span>
              }
              count={counts.erro}
              checked={f.status.includes("erro")}
              onToggle={() =>
                updateRevisaoFilters({ status: toggleArrayItem(f.status, "erro") })
              }
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <CheckCircle2 width="1.1em" height="1.1em" className="text-amber-600 dark:text-amber-400" />
                  Anuladas
                </span>
              }
              count={counts.anulada}
              checked={f.status.includes("anulada")}
              onToggle={() =>
                updateRevisaoFilters({ status: toggleArrayItem(f.status, "anulada") })
              }
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <Circle width="1.1em" height="1.1em" className="text-muted-foreground" />
                  Não respondidas
                </span>
              }
              count={counts.pendente}
              checked={f.status.includes("pendente")}
              onToggle={() =>
                updateRevisaoFilters({ status: toggleArrayItem(f.status, "pendente") })
              }
            />
          </FilterSection>

          {/* Dificuldade */}
          <FilterSection title="Dificuldade">
            {[1, 2, 3, 4, 5].map((d) => (
              <FilterRow
                key={d}
                label={<DifficultyStars value={d} />}
                checked={f.dificuldade.includes(d)}
                onToggle={() =>
                  updateRevisaoFilters({
                    dificuldade: toggleArrayItem(f.dificuldade, d)
                  })
                }
              />
            ))}
          </FilterSection>

          {/* Conteúdo */}
          <FilterSection title="Conteúdo">
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <AlignLeft width="1.3em" height="1.3em" className="text-sky-500" /> Com situação
                </span>
              }
              checked={f.somenteCaso}
              onToggle={() => updateRevisaoFilters({ somenteCaso: !f.somenteCaso })}
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <ImageIcon width="1.3em" height="1.3em" className="text-violet-500" /> Com imagem
                </span>
              }
              checked={f.somenteImagem}
              onToggle={() => updateRevisaoFilters({ somenteImagem: !f.somenteImagem })}
            />
            <FilterRow
              label={
                <span class="inline-flex items-center gap-1.5">
                  <TableIcon width="1.3em" height="1.3em" className="text-teal-500" /> Com tabela
                </span>
              }
              checked={f.somenteTabela}
              onToggle={() => updateRevisaoFilters({ somenteTabela: !f.somenteTabela })}
            />
          </FilterSection>

          {temas.length > 0 && (
            <FilterSection title={`Tema (${temas.length})`}>
              <div class="max-h-48 space-y-0.5 overflow-y-auto scroll-thin pr-1">
                {temas.map((t) => (
                  <FilterRow
                    key={t}
                    label={t}
                    checked={f.temas.includes(t)}
                    onToggle={() =>
                      updateRevisaoFilters({ temas: toggleArrayItem(f.temas, t) })
                    }
                  />
                ))}
              </div>
            </FilterSection>
          )}

          {fontes.length > 0 && (
            <FilterSection title={`Fonte (${fontes.length})`}>
              <div class="max-h-48 space-y-0.5 overflow-y-auto scroll-thin pr-1">
                {fontes.map((ft) => (
                  <FilterRow
                    key={ft}
                    label={ft}
                    checked={f.fontes.includes(ft)}
                    onToggle={() =>
                      updateRevisaoFilters({ fontes: toggleArrayItem(f.fontes, ft) })
                    }
                  />
                ))}
              </div>
            </FilterSection>
          )}
        </div>
      </aside>
    </div>
  );
}

// =====================
// Banner de "Avaliação da IA" — exibe o cache se já tiver sido gerado no
// Result. Caso contrário, mostra um CTA leve apontando o usuário pro Resultado
// (onde a geração tem o resumo completo de stats/breakdown). Colapsável.
// =====================

function UngradedEssaysBanner({ state }: { state: ProvaRevisaoState }) {
  return (
    <UngradedEssaysBannerShared
      prova={state.prova}
      essayResponses={state.essayResponses}
      bankId={state.bankId}
      provaId={state.provaId}
      onAfterRetry={(fresh) => {
        const cur = provaRevisaoView.value;
        if (cur && cur.provaId === state.provaId) {
          provaRevisaoView.value = {
            ...cur,
            prova: fresh.prova,
            questoes: fresh.questoes,
            essayResponses: fresh.essayResponses ?? cur.essayResponses
          };
        }
      }}
    />
  );
}

/** Banner reaproveitado em ProvaRevisaoView e ProvaResultView. Aparece quando
 *  há essays com texto mas sem nota da IA — comum quando o auto-grade do
 *  bank-prova-end falhou (timeout/rate-limit). Permite re-rodar manualmente
 *  sem depender do retry automático único por device. [[bug Karol 2026-05-25]] */
export function UngradedEssaysBannerShared({
  prova,
  essayResponses,
  bankId,
  provaId,
  onAfterRetry
}: {
  prova: ProvaState | null;
  essayResponses: Record<string, EssayResponseState>;
  bankId: string;
  provaId: string;
  onAfterRetry: (fresh: {
    prova: ProvaState;
    questoes: QuestaoStudy[];
    essayResponses?: Record<string, EssayResponseState>;
  }) => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    graded: number;
    failed: number;
  } | null>(null);

  const ungraded = useMemo(() => {
    if (!prova) return [];
    const touched = new Set(
      Array.isArray(prova.essayKeysTouched) ? prova.essayKeysTouched : []
    );
    const bucket = essayResponses ?? {};
    const list: string[] = [];
    for (const key of touched) {
      const ent = bucket[key];
      if (!ent) continue;
      const hasText =
        typeof ent.resposta === "string" && ent.resposta.trim().length > 0;
      if (hasText && ent.nota == null) list.push(key);
    }
    return list;
  }, [prova, essayResponses]);

  if (ungraded.length === 0 && !result) return null;

  async function retry() {
    setRetrying(true);
    setResult(null);
    try {
      const r = await api.bankProvaGradeRetry(bankId, provaId);
      setResult({ ok: true, graded: r.graded, failed: r.failed });
      try {
        const fresh = await api.bankProvaDetail(bankId, provaId);
        onAfterRetry({
          prova: fresh.prova,
          questoes: fresh.questoes,
          essayResponses: fresh.essayResponses
        });
      } catch {
        /* reload falhou — bucket já está no DB */
      }
    } catch (err) {
      setResult({ ok: false, graded: 0, failed: ungraded.length });
      console.warn("[ungraded-banner] retry falhou:", err);
    } finally {
      setRetrying(false);
    }
  }

  if (result?.ok && result.graded > 0 && ungraded.length === 0) {
    return (
      <div class="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
        <CheckCircle2 className="size-4 shrink-0" />
        <span>
          {result.graded}{" "}
          {result.graded === 1 ? "questão corrigida" : "questões corrigidas"}{" "}
          pela IA. As notas já apareceram acima.
        </span>
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
        <AlertCircle className="mt-0.5 size-4 shrink-0" />
        <div>
          <div class="font-medium">
            {ungraded.length === 1
              ? "1 questão dissertativa ficou sem nota."
              : `${ungraded.length} questões dissertativas ficaram sem nota.`}
          </div>
          <div class="text-[11px] opacity-80">
            A IA falhou em corrigir no momento da entrega. Sua resposta foi
            salva — basta tentar de novo.
          </div>
          {result && !result.ok && (
            <div class="mt-1 text-[11px] font-medium text-red-700 dark:text-red-300">
              Nova tentativa falhou. Tente de novo em alguns minutos.
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        title="Re-corrigir com IA"
        aria-label="Re-corrigir com IA"
        onClick={() => void retry()}
        disabled={retrying}
        class="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-amber-700 disabled:opacity-60"
      >
        {retrying ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Sparkles className="size-3.5" />
        )}
        {retrying ? "Re-corrigindo…" : "Re-corrigir com IA"}
      </button>
    </div>
  );
}

function AiAnalysisBanner({
  bankId,
  provaId
}: {
  bankId: string;
  provaId: string;
}) {
  const [cache, setCache] = useState<ReturnType<typeof readAiAnalysisCache>>(
    () => readAiAnalysisCache(provaId)
  );
  // Hidrata do server (cobre cross-device)
  useEffect(() => {
    let cancelled = false;
    void fetchAiAnalysisFromServer(bankId, provaId).then((serverCache) => {
      if (cancelled || !serverCache) return;
      const local = readAiAnalysisCache(provaId);
      if (
        !local ||
        new Date(serverCache.generatedAt).getTime() >
          new Date(local.generatedAt).getTime()
      ) {
        writeAiAnalysisCache(provaId, serverCache);
        setCache(serverCache);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [bankId, provaId]);
  const [expanded, setExpanded] = useState(false);
  if (!cache) {
    // Sem cache: link discreto pro Result onde fica o gerador
    return (
      <button
        type="button"
        onClick={() => {
          const st = provaRevisaoView.value;
          if (st) backToResult(st);
        }}
        class="flex w-full items-center gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-left text-xs text-primary hover:bg-primary/10"
      >
        <span class="grid size-6 shrink-0 place-items-center rounded-md bg-primary/15">
          ★
        </span>
        <span class="flex-1">
          <strong>Avaliação da IA</strong> — disponível no resumo final
          (Resultado). Clique pra ir.
        </span>
      </button>
    );
  }
  return (
    <section class="rounded-md border border-primary/40 bg-primary/5">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        class="flex w-full items-center gap-3 px-3 py-2 text-left"
      >
        <span class="grid size-6 shrink-0 place-items-center rounded-md bg-primary/15 text-primary text-xs font-bold">
          ★
        </span>
        <div class="min-w-0 flex-1">
          <div class="text-[11px] font-bold uppercase tracking-wider text-primary">
            Avaliação da IA
          </div>
          <div class="truncate text-[10px] text-muted-foreground">
            gerada em {new Date(cache.generatedAt).toLocaleString("pt-BR")}
          </div>
        </div>
        <span class="shrink-0 text-[11px] text-muted-foreground">
          {expanded ? "Recolher" : "Expandir"}
        </span>
      </button>
      {expanded && (
        <div class="border-t border-primary/20 px-4 py-3">
          <div class="mb-2 flex items-center gap-1.5">
            <CopyAiTextButton text={cache.text} />
          </div>
          <AiMarkdownBlock text={cache.text} />
        </div>
      )}
    </section>
  );
}

// =====================
// Header da revisão
// =====================

function backToResult(state: ProvaRevisaoState) {
  exitProvaRevisaoView();
  const url = `/student/banks/${encodeURIComponent(state.bankId)}/prova/${encodeURIComponent(
    state.provaId
  )}/resultado`;
  window.history.pushState({ provaResultId: state.provaId }, "", url);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function RevisaoHeader({
  state,
  counts,
  currentIdx,
  total
}: {
  state: ProvaRevisaoState;
  counts: { acertos: number; erros: number; pendentes: number; anuladas: number; total: number };
  currentIdx: number;
  total: number;
}) {
  const listCollapsed = uiPrefs.value.questionListCollapsed;
  const aiEnabled = uiPrefs.value.aiAssistantEnabled;
  const aiOpen = uiPrefs.value.aiAssistantOpen;
  const actions: WorkspaceHeaderAction[] = [
    {
      id: "voltar",
      icon: ArrowLeft,
      label: "Voltar",
      title: "Voltar para o resultado (Esc)",
      onClick: () => backToResult(state)
    },
    {
      id: "lista",
      icon: ListIcon,
      label: listCollapsed ? "Mostrar lista" : "Esconder lista",
      title: "Lista de questões (L)",
      onClick: () => toggleQuestionListCollapsed()
    },
    {
      id: "filtros",
      icon: SlidersHorizontal,
      label: "Filtros",
      title: "Filtrar lista por status (acerto / erro / anulada / pendente)",
      onClick: () => (revisaoFiltersOpen.value = true),
      tone: countRevisaoFiltersActive() > 0 ? "amber" : "neutral",
      badge:
        countRevisaoFiltersActive() > 0
          ? String(countRevisaoFiltersActive())
          : undefined
    },
    {
      id: "mapa",
      icon: Grid3x3,
      label: "Mapa",
      title: "Mapa de questões (M)",
      onClick: () => (revisaoMapOpen.value = true)
    },
    // Toggle pro Assistente IA / Anotações — só aparece se o aluno tem IA
    // habilitada. Mesmo comportamento do Modo Estudo (uiPrefs compartilhado).
    ...(aiEnabled
      ? ([{
          id: "ai",
          icon: Sparkles,
          label: aiOpen ? "Esconder IA" : "Tutor IA",
          title: "Tutor IA + Anotações (usa contexto da questão atual com sua resposta marcada)",
          onClick: () => toggleAiAssistantOpen()
        }] as WorkspaceHeaderAction[])
      : [])
  ];
  return (
    <WorkspaceHeader
      mode="revisao"
      icon={Flag}
      iconTone="violet"
      title={state.bank?.name ?? "—"}
      subtitle={
        <>
          Revisão · Questão {currentIdx + 1} de {total}
        </>
      }
      summary={
        <div class="flex flex-wrap items-center gap-1.5 text-[11px]">
          <CountChip label="Acertos" value={counts.acertos} color="emerald" />
          <CountChip label="Erros" value={counts.erros} color="red" />
          {counts.anuladas > 0 && (
            <CountChip
              label="Anuladas"
              value={counts.anuladas}
              color="amber"
            />
          )}
          <CountChip label="Pendentes" value={counts.pendentes} color="muted" />
        </div>
      }
      actions={actions}
    />
  );
}

function CountChip({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: "emerald" | "red" | "muted" | "amber";
}) {
  const cls =
    color === "emerald"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : color === "red"
        ? "bg-red-500/15 text-red-600 dark:text-red-400"
        : color === "amber"
          ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
          : "bg-muted text-muted-foreground";
  return (
    <span class={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold ${cls}`}>
      <span class="tabular-nums">{value}</span>
      <span class="opacity-80">{label}</span>
    </span>
  );
}

// =====================
// IA: explica a questão pro aluno (especialmente em erro/anulada/pendente)
// =====================
// Botão "Explicar com IA" que dispara uma chamada ao /api/student/ai-chat
// com prompt automático contextual. Persistência por (bankId, provaId, qid)
// via aiExplainCache — aluno reabre a questão e a explicação já está lá.
// Adicionalmente injeta o texto no chatHistory[qid] como uma "fala" inicial
// do tutor, pra continuidade quando ele abrir o Tutor IA do lado. Pedido
// 2026-05-24: "não quero que pareça pra ele que cada um é uma coisa separada".

/** Marca usada pra detectar se o turn no chat já é a explicação injetada
 *  (evita duplicar quando hidrata várias vezes). Invisível ao aluno —
 *  fica embutido no início do conteúdo e é renderizado normalmente. */
const EXPLAIN_INJECT_MARK = "<!--ai-explain-injected-->";

function ensureExplanationInChatHistory(qid: string, text: string): void {
  if (!qid || !text) return;
  const cur = chatHistory.value[qid] ?? [];
  // Se já há QUALQUER turn injetado pra essa questão, atualiza-o em vez de
  // duplicar. Caso contrário, prepende como primeira fala do assistente.
  const idx = cur.findIndex(
    (t) => t.role === "assistant" && typeof t.content === "string" && t.content.includes(EXPLAIN_INJECT_MARK)
  );
  const injected = `${EXPLAIN_INJECT_MARK}\n${text}`;
  let next: typeof cur;
  if (idx >= 0) {
    if (cur[idx]?.content === injected) return; // já igual, no-op
    next = cur.slice();
    next[idx] = { role: "assistant", content: injected };
  } else {
    next = [{ role: "assistant", content: injected }, ...cur];
  }
  chatHistory.value = { ...chatHistory.value, [qid]: next };
}

export function AiExplainBlock({
  q,
  respostaIdx,
  corretaIdx,
  status,
  bankId,
  provaId
}: {
  q: QuestaoStudy;
  respostaIdx: number | null;
  corretaIdx: number;
  status: QuestaoStatus;
  /** Quando passados, ativam persistência (localStorage + server) + injeção
   *  no chatHistory do Tutor IA. Sem eles, o bloco funciona como antes —
   *  só explicação volátil, sem cross-device, sem ligação com chat.
   *  Pedido 2026-05-24 ("guardar a correção por questão e injetar no chat"). */
  bankId?: string | null;
  provaId?: string | null;
}) {
  const [phase, setPhase] = useState<"idle" | "loading" | "done" | "error">(
    "idle"
  );
  const [text, setText] = useState<string>("");
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [err, setErr] = useState<FriendlyAiError | null>(null);

  // Hidratação ao montar / trocar de questão. Tenta localStorage primeiro
  // (instantâneo), depois server (cobre cross-device). Em ambos os casos
  // sincroniza ChatHistory pra explicação aparecer como contexto inicial
  // do Tutor IA — quando o aluno abrir a aba lateral, a IA já "sabe" o
  // que foi explicado ali, sem dois pensamentos paralelos.
  useEffect(() => {
    setPhase("idle");
    setText("");
    setGeneratedAt(null);
    setErr(null);

    if (!bankId || !provaId) return;
    const local = readAiExplainCache(bankId, provaId, q.id);
    if (local) {
      setText(local.text);
      setGeneratedAt(local.generatedAt);
      setPhase("done");
      ensureExplanationInChatHistory(q.id, local.text);
    }
    // Server sync (mesmo que tenha cache local, atualiza se houver versão
    // mais recente — espelha o que aiAnalysisCache faz). Não bloqueia o UI.
    let cancelled = false;
    void (async () => {
      const server = await fetchAiExplainFromServer(bankId, provaId, q.id);
      if (cancelled || !server) return;
      const localNow = readAiExplainCache(bankId, provaId, q.id);
      const localTs = localNow
        ? new Date(localNow.generatedAt).getTime()
        : 0;
      const serverTs = new Date(server.generatedAt).getTime();
      if (serverTs > localTs) {
        writeAiExplainCache(bankId, provaId, q.id, server);
        setText(server.text);
        setGeneratedAt(server.generatedAt);
        setPhase("done");
        ensureExplanationInChatHistory(q.id, server.text);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q.id, bankId, provaId]);

  async function ask() {
    setPhase("loading");
    setErr(null);
    setText("");
    try {
      const alts: string[] = Array.isArray((q as any).alternativas)
        ? (q as any).alternativas
        : Array.isArray((q as any).opcoes)
          ? (q as any).opcoes
          : [];
      const letras = ["A", "B", "C", "D", "E", "F"];
      const minhaLetra =
        respostaIdx != null && respostaIdx >= 0 ? letras[respostaIdx] : null;
      const corretaLetra =
        corretaIdx >= 0 ? letras[corretaIdx] : null;

      // O bloco da revisão NÃO é conversacional. A IA entrega um veredito
      // explicativo sobre a questão — pra ler e copiar em outra plataforma
      // se quiser. Nada de "me pergunte mais", "quer outra questão", etc.
      const regraSemConversa =
        "Escreva uma explicação fechada (não é conversa). " +
        "Use **negrito** pra termos-chave, listas pra passos, e $...$ pra expressões matemáticas se houver. " +
        "NÃO sugira 'gerar mais questões', 'me pergunte mais', 'continue estudando na plataforma' nem peça interação. " +
        "NÃO use frases como 'estou aqui para ajudar', 'qualquer dúvida'. " +
        "Tom de professor entregando explicação completa.";

      // Seções extras (conceitos + referências) — pedido 2026-05-24 pra que
      // a explicação não seja só sobre a questão isolada, mas dispare estudo
      // dirigido. Aplicada em todos os status (acerto, erro, anulada, pendente)
      // — útil mesmo quando o aluno acertou (pra fixar nomenclatura técnica).
      const estruturaObrigatoria =
        "\n\nESTRUTURA OBRIGATÓRIA (cabeçalhos markdown idênticos a estes):" +
        "\n\n## Explicação" +
        "\n(análise didática conforme a pergunta abaixo. Linguagem **progressiva** — do simples ao mais profundo, ancorando conceitos abstratos em **analogias do mundo real** quando possível. Esse estilo analógico foi elogiado pela Karol em 2026-05-24 — mantenha.)" +
        "\n\n## Conceitos técnicos cobrados" +
        "\nLista BULLETED dos **nomes técnicos exatos** (em negrito) dos conceitos que essa questão cobra — " +
        "os termos pelos quais o aluno deve PESQUISAR pra estudar mais. Ex: '**Lei de Frank-Starling**', " +
        "'**vias aferentes do reflexo barorreceptor**', '**oração subordinada substantiva apositiva**'. " +
        "Cada bullet = 1 conceito + 1 frase curta explicando o que é e por que apareceu. " +
        "Entre 3 e 6 bullets. Esses nomes são o que o aluno vai digitar no Google/PubMed/biblioteca." +
        "\n\n## Onde estudar mais" +
        "\nReferências EXTERNAS reais agrupadas por tipo (bullets):" +
        "\n- **Livros**: 1–3 obras conhecidas da área (autor + título — Robbins Patologia, Goldman-Cecil, Harrison, Cury, Iturricha, etc., adaptados à matéria inferida da questão). Cite capítulo/seção se for óbvio." +
        "\n- **Sites e bases**: 1–3 fontes confiáveis por nome (PubMed, SciELO, UpToDate, Khan Academy, Brasil Escola, Mundo Educação, Stoodi, etc.). NÃO invente URLs específicas, só o nome do site." +
        "\n- **Termos de busca**: 2–4 frases prontas que o aluno deve digitar (em itálico). Ex: *'mecanismo de ação dos beta-bloqueadores no nó AV'*. Use os conceitos da seção anterior." +
        "\nAdapte tudo à área inferida pelo tema/enunciado.";

      let pergunta = "";
      if (status === "erro" && minhaLetra && corretaLetra) {
        pergunta = `Eu marquei a alternativa ${minhaLetra}, mas a correta é a ${corretaLetra}. Na **## Explicação**, faça didaticamente: (1) por que a ${minhaLetra} está errada, (2) por que a ${corretaLetra} é a correta, (3) qual confusão conceitual provavelmente levou ao erro.`;
      } else if (status === "anulada") {
        pergunta = `Esta questão foi anulada. Na **## Explicação**, descreva o conteúdo principal abordado e o que eu deveria saber sobre o tópico — como se ela ainda valesse.`;
      } else if (status === "pendente" && corretaLetra) {
        pergunta = `Eu não respondi essa questão. Na **## Explicação**, mostre o raciocínio correto pra chegar à alternativa ${corretaLetra}, passo a passo.`;
      } else {
        pergunta = `Na **## Explicação**, descreva conceitos principais, raciocínio, e por que a alternativa correta é a melhor.`;
      }
      const prompt = pergunta + estruturaObrigatoria + " " + regraSemConversa;

      const resp = await api.aiChat({
        question: {
          id: q.id,
          tema: q.tema ?? null,
          fonte: q.fonte ?? null,
          pergunta: q.pergunta ?? null,
          caso: q.caso ?? null,
          alternativas: alts,
          correta: q.correta ?? null,
          resolucao: (q as any).resolucao ?? null
        },
        history: [],
        message: prompt,
        respostaAluno: respostaIdx,
        verbosity: activeVerbosity()
      });
      const reply = resp.reply ?? "";
      setText(reply);
      setPhase("done");
      // Persiste se temos contexto de prova (cache + server). Fora desse
      // contexto (chamada legada sem bankId/provaId), só state local.
      if (bankId && provaId) {
        const now = new Date().toISOString();
        setGeneratedAt(now);
        const cache = { text: reply, generatedAt: now };
        writeAiExplainCache(bankId, provaId, q.id, cache);
        void saveAiExplainToServer(bankId, provaId, q.id, cache);
        ensureExplanationInChatHistory(q.id, reply);
      }
    } catch (e) {
      setErr(friendlyAiError(e));
      setPhase("error");
    }
  }

  if (phase === "idle") {
    return (
      <button
        type="button"
        onClick={ask}
        class="group flex w-full items-center gap-2.5 rounded-md border border-primary/40 bg-primary/5 px-4 py-3 text-left transition-colors hover:border-primary/60 hover:bg-primary/10"
      >
        <span class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/30">
          <Sparkles className="size-4" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-bold text-primary">
            Explicar com IA
          </span>
          <span class="block text-[11px] text-muted-foreground">
            {status === "erro"
              ? "Por que errei e como acertar"
              : status === "anulada"
                ? "Entender o tópico mesmo com a questão anulada"
                : "Raciocínio passo a passo"}
          </span>
        </span>
        <span class="shrink-0 text-primary/60 transition-transform group-hover:translate-x-0.5">
          ›
        </span>
      </button>
    );
  }

  return (
    <div class="rounded-md border border-primary/40 bg-primary/5 px-4 py-3">
      <div class="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-primary">
        <Sparkles className="size-3.5" />
        Explicação da IA
        <VerbosityChip size="xs" />
        {phase === "loading" && (
          <Loader2 className="ml-1 size-3 animate-spin" />
        )}
        {phase === "done" && text && (
          <span class="ml-auto flex items-center gap-1">
            {generatedAt && (
              <span class="text-[10px] font-normal normal-case text-muted-foreground">
                gerada em {new Date(generatedAt).toLocaleString("pt-BR")}
              </span>
            )}
            <CopyAiTextButton text={text} size="xs" />
            {bankId && provaId && (
              <button
                type="button"
                onClick={() => {
                  // Regerar: limpa cache (local + server) + chama de novo. NÃO
                  // limpa o chatHistory inteiro — só sobrescreve o turn injetado
                  // dentro de ensureExplanationInChatHistory.
                  deleteAiExplainCache(bankId, provaId, q.id);
                  void deleteAiExplainFromServer(bankId, provaId, q.id);
                  setText("");
                  setGeneratedAt(null);
                  setPhase("idle");
                  void ask();
                }}
                title="Regerar explicação (substitui a salva)"
                class="rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                Regerar
              </button>
            )}
            <button
              type="button"
              onClick={() => setPhase("idle")}
              class="rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              Fechar
            </button>
          </span>
        )}
        {phase === "error" && (
          <button
            type="button"
            onClick={() => setPhase("idle")}
            class="ml-auto rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            Fechar
          </button>
        )}
      </div>
      {phase === "loading" && (
        <p class="text-xs text-muted-foreground">
          A IA está montando a explicação… (geralmente 5–15 segundos)
        </p>
      )}
      {phase === "error" && (
        <div class="space-y-2">
          {err ? (
            <>
              <p class="text-xs font-semibold text-destructive">{err.title}</p>
              <p class="text-xs leading-relaxed text-destructive/90">{err.message}</p>
            </>
          ) : (
            <p class="text-xs text-destructive">Erro ao gerar explicação.</p>
          )}
          <button
            type="button"
            onClick={ask}
            class="rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            {err?.actionLabel ?? "Tentar de novo"}
          </button>
        </div>
      )}
      {phase === "done" && <AiMarkdownBlock text={text} />}
    </div>
  );
}

// =====================
// Navegação inferior
// =====================

function RevisaoNav({
  idx,
  total,
  onPrev,
  onNext
}: {
  idx: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <nav class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={idx === 0}
        title="Anterior (←)"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft width="1.2em" height="1.2em" />
        Anterior
      </button>
      <span class="text-xs tabular-nums text-muted-foreground">
        {idx + 1} / {total}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={idx >= total - 1}
        title="Próxima (→)"
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        Próxima
        <ChevronRight width="1.2em" height="1.2em" />
      </button>
    </nav>
  );
}

// =====================
// Mapa de questões clicável
// =====================

function RevisaoMapModal({ state }: { state: ProvaRevisaoState }) {
  const prova = state.prova!;
  const escopo = useMemo(() => {
    if (prova.escopoQuestaoIds.length === 0) return state.questoes;
    return filterEscopo(state.questoes, prova.escopoQuestaoIds);
  }, [state.questoes, prova]);
  return (
    <div
      class="fixed inset-0 z-[55] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={() => (revisaoMapOpen.value = false)}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Grid3x3 width="1.3em" height="1.3em" className="text-primary" />
            Mapa de questões
          </h2>
          <button
            type="button"
            onClick={() => (revisaoMapOpen.value = false)}
            class="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-accent"
          >
            Fechar
          </button>
        </header>
        <div class="overflow-y-auto p-5 scroll-thin">
          <div class="grid grid-cols-5 gap-2 lg:grid-cols-10">
            {escopo.map((q, i) => (
              <RevisaoMapCell
                key={q.id}
                q={q}
                prova={prova}
                essayResponses={state.essayResponses}
                num={i + 1}
                isCurrent={i === state.currentIdx}
                onClick={() => {
                  gotoIdx(i, escopo.length);
                  revisaoMapOpen.value = false;
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RevisaoMapCell({
  q,
  prova,
  essayResponses,
  num,
  isCurrent,
  onClick
}: {
  q: QuestaoStudy;
  prova: ProvaState;
  essayResponses: Record<string, EssayResponseState>;
  num: number;
  isCurrent: boolean;
  onClick: () => void;
}) {
  const st = statusOf(q, prova, essayResponses);
  const hasImg = questaoHasImages(q);
  const hasTab = questaoHasTabelas(q);
  const tone =
    st === "acerto"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : st === "erro"
        ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"
        : st === "anulada"
          ? "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300"
          : "border-border bg-card/60 text-foreground/80 hover:bg-accent/60";
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Questão ${num}${q.tema ? ` · ${q.tema}` : ""}${hasImg ? " · imagem" : ""}${hasTab ? " · tabela" : ""}`}
      class={`relative flex aspect-square min-h-[56px] flex-col items-center justify-center rounded-md border text-sm font-bold transition-all ${tone} ${
        isCurrent ? "ring-2 ring-primary scale-105" : ""
      }`}
    >
      <span class="leading-none">{num}</span>
    </button>
  );
}
